/**
 * API Client for EnterPress Landing Page
 * 
 * Handles communication with the Starbase API (waitlist, translation)
 */

const API_BASE_URL = import.meta.env.VITE_STARBASE_API_URL || import.meta.env.VITE_WAITLIST_API_URL || 'http://localhost:3005';

// ============================================
// Waitlist API
// ============================================

export interface WaitlistResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Add an email to the waitlist
 */
export async function addToWaitlist(email: string): Promise<WaitlistResponse> {
  let response: Response;
  
  try {
    response = await fetch(`${API_BASE_URL}/waitlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        source: 'landing-page',
      }),
    });
  } catch {
    // Network error or CORS blocked (often happens with 429 from Cloud Armor)
    throw new ApiError(
      'Unable to reach the server. Please try again in a moment.',
      0,
      'NETWORK_ERROR'
    );
  }

  // Handle rate limiting (Cloud Armor returns HTML, not JSON)
  if (response.status === 429) {
    throw new ApiError(
      'Too many requests. Please wait a moment and try again.',
      429,
      'RATE_LIMITED'
    );
  }

  // Try to parse JSON response
  let data: WaitlistResponse;
  try {
    data = await response.json();
  } catch {
    // Non-JSON response (e.g., HTML error page)
    throw new ApiError(
      'Unexpected server response. Please try again.',
      response.status,
      'PARSE_ERROR'
    );
  }

  if (!response.ok) {
    throw new ApiError(
      data.error || 'Failed to join waitlist',
      response.status,
      data.code
    );
  }

  return data;
}

// ============================================
// Translation API
// ============================================

export type Language = 'en' | 'bn' | 'hi' | 'es' | 'fr' | 'de' | 'zh' | 'ja' | 'ko' | 'ar';

export interface TranslationResult {
  success: boolean;
  original: string;
  translated: string;
  language: Language;
  cached: boolean;
  processingTimeMs?: number;
  error?: string;
}

export interface BatchTranslationResult {
  success: boolean;
  translations: {
    original: string;
    translated: string;
    cached: boolean;
  }[];
  language: Language;
  totalProcessingTimeMs?: number;
  cacheHitRate?: number;
  error?: string;
}

/**
 * Translate a single text string
 */
export async function translateText(text: string, targetLang: Language): Promise<TranslationResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, targetLang }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(
        error.error || 'Translation failed',
        response.status
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Translation service unavailable', 0, 'NETWORK_ERROR');
  }
}

/**
 * Translate multiple texts in a single request
 */
export async function translateBatch(texts: string[], targetLang: Language): Promise<BatchTranslationResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/translate/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ texts, targetLang }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(
        error.error || 'Batch translation failed',
        response.status
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Translation service unavailable', 0, 'NETWORK_ERROR');
  }
}

/**
 * Get supported languages
 */
export async function getSupportedLanguages(): Promise<{ code: string; name: string }[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/translate/languages`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.languages || [];
  } catch {
    return [];
  }
}

// ============================================
// Health Check
// ============================================

/**
 * Check API health
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
